import TileLayer from 'ol/layer/VectorTile';
import { applyStyle, applyBackground } from 'ol-mapbox-style';

import { AbstractLayer, AbstractLayerProps, LayerProps } from './AbstractLayer';

/**
 * AbstractLayer implementation for Mapbox-GL-styled ArcGIS Vector Tiles.
 * Uses ol-mapbox-style to parse the Esri style JSON and configure both the
 * tile source and the per-layer rendering rules (sprites, glyphs, filters).
 */
// ol/layer/VectorTile in OL 6 is not generic (source type is fixed to VectorTile),
// so the AbstractLayer generic is satisfied with just TileLayer.
export class VectorTileLayer extends AbstractLayer<TileLayer> {
    constructor(props: AbstractLayerProps) {
        super(props);
    }

    // ArcGIS VectorTileServer serves HTML from its root URL unless `?f=json`
    // is appended. The Esri style JSON points sources at that root, which
    // makes ol-mapbox-style's TileJSON fetch fail. We side-step that by
    // pre-fetching the style, resolving the tile template from the server's
    // JSON metadata, and rewriting the source to use direct `tiles` URLs.
    private async loadArcgisStyle(layer: TileLayer, styleUrl: string) {
        const styleRes = await fetch(styleUrl);
        if (!styleRes.ok)
            throw new Error(`style fetch failed: ${styleRes.status}`);
        const style = await styleRes.json();

        const [sourceName, source] =
            Object.entries(style.sources ?? {})[0] ?? [];
        if (!sourceName || !source) throw new Error('style has no sources');

        const sourceBase = new URL(
            (source as { url?: string }).url ?? '.',
            styleUrl,
        ).toString();
        const infoRes = await fetch(
            `${sourceBase}${sourceBase.includes('?') ? '&' : '?'}f=json`,
        );
        if (!infoRes.ok)
            throw new Error(`server info fetch failed: ${infoRes.status}`);
        const info = await infoRes.json();

        const tileTemplate: string | undefined = info.tiles?.[0];
        if (!tileTemplate)
            throw new Error('server info missing tiles template');

        // Build the absolute tile URL via string concat rather than `new URL()`:
        // the WHATWG URL constructor percent-encodes `{` and `}` to `%7B`/`%7D`,
        // which breaks OpenLayers' tile-template substitution (it matches the
        // literal `{z}` / `{x}` / `{y}` placeholders).
        const absoluteTileTemplate = /^https?:\/\//i.test(tileTemplate)
            ? tileTemplate
            : `${sourceBase.replace(/\/+$/, '')}/${tileTemplate.replace(/^\/+/, '')}`;

        style.sources[sourceName] = {
            ...(source as object),
            url: undefined,
            tiles: [absoluteTileTemplate],
            minzoom: info.minLOD ?? (source as { minzoom?: number }).minzoom,
            maxzoom: info.maxLOD ?? (source as { maxzoom?: number }).maxzoom,
        };

        await applyStyle(layer, style, sourceName, styleUrl);
        await applyBackground(layer, style).catch(() => {});
    }

    initializeLayer({ visible, opacity, zIndex }: LayerProps): TileLayer {
        const layer = new TileLayer({
            visible,
            opacity,
            zIndex,
            declutter: true,
        });
        if (this.config.styleUrl) {
            this.loadArcgisStyle(layer, this.config.styleUrl).catch((err) => {
                console.error(
                    `Failed to apply vector tile style for ${this.config.id}:`,
                    err,
                );
            });
        }
        return layer;
    }

    applyAuthentication() {}
}

export default VectorTileLayer;
